import Cookies from 'js-cookie';

interface CookieOptions {
  expires?: number | Date;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  domain?: string;
}

class CookieService {
  private defaultOptions: CookieOptions = {
    secure: true, // HTTPS only
    sameSite: 'Lax', // CSRF protection while allowing top-level navigation
    // domain will be set dynamically based on environment
  };

  /**
   * Get the appropriate cookie domain based on the current hostname
   * Returns undefined to scope cookies to the exact hostname only.
   * This ensures staging (staging.inocu.de) and production (app.inocu.de)
   * have separate cookies and don't interfere with each other.
   */
  private getCookieDomain(): string | undefined {
    return undefined;
  }

  /**
   * Set a cookie with secure defaults
   * @param key Cookie name
   * @param value Cookie value
   * @param options Optional cookie settings (expires, secure, sameSite, domain)
   */
  set(key: string, value: string, options: CookieOptions = {}): void {
    const cookieOptions: CookieOptions = {
      ...this.defaultOptions,
      domain: this.getCookieDomain(),
      ...options,
    };

    Cookies.set(key, value, cookieOptions);
  }

  /**
   * Get a cookie value
   * @param key Cookie name
   * @returns Cookie value or null if not found
   */
  get(key: string): string | null {
    const value = Cookies.get(key);
    return value !== undefined ? value : null;
  }

  /**
   * Remove a specific cookie
   * @param key Cookie name
   */
  remove(key: string): void {
    // Remove with domain option to ensure proper cleanup
    const domain = this.getCookieDomain();
    if (domain) {
      Cookies.remove(key, { domain });
    }
    // Also try without domain as fallback
    Cookies.remove(key);
  }

  /**
   * Clear all application cookies
   * Removes all known authentication and session cookies
   */
  clear(): void {
    const knownCookies = [
      'access_token',
      'refresh_token',
      'tenant',
      'selectedCustomerId',
      'is_hijack_session',
    ];

    knownCookies.forEach(key => this.remove(key));
  }

  /**
   * Set a session cookie (expires when browser closes)
   * @param key Cookie name
   * @param value Cookie value
   */
  setSessionCookie(key: string, value: string): void {
    this.set(key, value, { expires: undefined });
  }

  /**
   * Set an authentication token with 30-day expiry
   * @param key Token name
   * @param value Token value
   */
  setAuthToken(key: string, value: string): void {
    this.set(key, value, { expires: 30 });
  }
}

export default new CookieService();
